import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import {
  Callout,
  CurrencyNote,
  FaqList,
  H2,
  H3,
  InsuranceCTA,
  P,
  SITE,
  SpokeCards,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE =
  "Employer & Marketplace GLP-1 Coverage | Virginia Bariatric";
const DESCRIPTION =
  "Sentara, UnitedHealthcare, Cigna, Anthem, Aetna, CareFirst, and Kaiser criteria for Zepbound and Wegovy — plus PBM formularies and marketplace exclusions.";
const URL = `${SITE}/medical-weight-loss/insurance/commercial-marketplace`;
const NAME = "Employer, Commercial, and Marketplace Coverage in Virginia";

const FAQS: Faq[] = [
  {
    q: "Do Virginia marketplace plans cover weight-loss medication?",
    a: "Generally no. Virginia's Essential Health Benefits benchmark plan specifically excludes weight-loss drugs, so individual and small-group plans in Virginia are not required to cover Zepbound, Wegovy, or Saxenda for weight loss and generally do not. A plan may still cover the same drug for a different FDA-approved indication such as sleep apnea or cardiovascular risk reduction.",
  },
  {
    q: "Why do two people with the same insurance card have different coverage?",
    a: "Most large employers self-fund their health plans and choose their own pharmacy benefits, so the carrier's published policy is only a default. On top of that, a pharmacy benefit manager — CVS Caremark, Express Scripts, or Optum Rx — decides which specific products sit on the formulary. Both layers can differ between two employees of different companies holding cards from the same insurer.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/insurance/commercial-marketplace")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance/commercial-marketplace") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({ url: URL, name: NAME, description: DESCRIPTION })
        ),
      },
    ] : [],
  }),
  component: CommercialPage,
});

function CommercialPage() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">
          Employer, Commercial, and Marketplace Plans
        </h1>
        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          Carrier-by-carrier criteria for Zepbound, Wegovy, and Saxenda &mdash; and the pharmacy
          benefit manager layer that can override them.
        </p>
        <TelehealthCta />

      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <CurrencyNote />
        <P>
          The{" "}
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="text-brand-deep underline hover:no-underline"
          >
            coverage overview
          </Link>{" "}
          explains the two gates every commercial plan applies before any of the criteria below
          matter.
        </P>

        <H2 id="commercial">Employer and commercial plans</H2>
        <P>
          This is where coverage varies the most, because most large employers self-fund their
          plans and set their own rules. Two people with the same insurance card and the same
          insurer can have completely different coverage.
        </P>

        <H3>Sentara Health Plans</H3>
        <P>
          Sentara&rsquo;s criteria (Weight Management Drugs prior authorization form, revised June
          11, 2026) are among the more attainable in Virginia:
        </P>
        <UL>
          <li>BMI of 30 or higher, or BMI of 27 or higher with a qualifying condition</li>
          <li>
            Participation in a weight-loss treatment plan &mdash; nutritional counseling, exercise,
            or a calorie-restricted diet &mdash; within the past six months, continuing during
            treatment
          </li>
          <li>Current height, weight, and BMI submitted by your provider</li>
          <li>Not using another GLP-1 concurrently</li>
        </UL>
        <P>
          <strong>Initial approval: 7 months.</strong> Renewal requires at least 5% weight loss
          during that period, or maintenance of that loss for subsequent renewals (which run up to
          12 months). Authorizations won&rsquo;t be renewed if BMI falls below 18.5. A formulation
          catch worth knowing: Sentara&rsquo;s form specifies Zepbound Pens and states that
          &ldquo;Kwikpen/vial formulations are NOT covered.&rdquo;
        </P>

        <H3>UnitedHealthcare</H3>
        <P>
          UnitedHealthcare&rsquo;s commercial criteria (Program 2026 P 1114-21, effective July 1,
          2026) apply only if your employer elected weight-loss coverage: BMI of 30 or higher, or 27
          or higher with a weight-related condition; used alongside lifestyle modification; age 12
          and up for Saxenda, Wegovy injection, and Qsymia, and over 16 for other products.
        </P>
        <P>
          <strong>Initial approval periods differ by drug:</strong> Saxenda gets 4 months, Wegovy
          injection or tablet gets 5 months, Zepbound gets 6 months. Renewal thresholds also differ
          &mdash; 4% weight loss for Saxenda, 5% for Wegovy and Zepbound &mdash; and renewals run 12
          months. Notably, UnitedHealthcare&rsquo;s standard commercial criteria do <strong>not</strong>{" "}
          require step therapy through an older weight-loss drug.
        </P>

        <H3>Cigna</H3>
        <P>
          Cigna&rsquo;s coverage policy IP0206, effective June 1, 2026, is titled &ldquo;Weight Loss
          &ndash; Glucagon-Like Peptide-1 Agonists BMI &ge; 30&rdquo;:
        </P>
        <UL>
          <li>Age 18 or older</li>
          <li>
            <strong>At least 3 months</strong> of behavioral modification and dietary restriction
          </li>
          <li>BMI of 30 or higher at baseline, or 27 or higher with a qualifying condition</li>
          <li>Used alongside behavioral modification and a reduced-calorie diet</li>
        </UL>
        <P>
          <strong>Initial approval:</strong> 8 months for Zepbound and Wegovy; 4 months for
          Saxenda. <strong>Renewal:</strong> 1 year, requiring at least 5% weight loss for Zepbound
          and Wegovy, or at least 4% for Saxenda. Cigna measures against your baseline{" "}
          <em>before any GLP-1</em>, which matters if you&rsquo;re switching products, and requires
          documentation for Saxenda specifically &mdash; chart notes, lab results, or prescription
          receipts.
        </P>

        <H3>Anthem Blue Cross and Blue Shield</H3>
        <P>
          Anthem is Virginia&rsquo;s largest commercial carrier, and coverage varies widely across
          its plan types. Anthem requires prior authorization whenever it covers these drugs. Its
          commercial criteria generally follow the BMI 30, or 27-with-comorbidity standard, with
          documented lifestyle attempts and a 5% weight-loss requirement for renewal &mdash; but
          many Anthem employer plans exclude weight-loss drugs from the pharmacy benefit entirely.
          Anthem&rsquo;s pharmacy benefits are administered by CarelonRx. Because employer groups
          make the final call, verify your specific plan rather than relying on general Anthem
          policy.
        </P>

        <H3>Aetna</H3>
        <P>
          Aetna covers Wegovy and Zepbound for chronic weight management under its clinical policy
          bulletins, generally at BMI 30 or higher, or 27 or higher with a weight-related condition,
          plus documented participation in a weight management program. Aetna maintains{" "}
          <strong>multiple prior authorization policies</strong> that employers can choose between
          &mdash; including a distinct &ldquo;Weight Loss (BMI 35)&rdquo; GLP-1 policy with a higher
          threshold. Aetna also administers the COVA HealthAware plan for{" "}
          <Link
            to="/medical-weight-loss/insurance/medicaid-cova"
            className="text-brand-deep underline hover:no-underline"
          >
            Virginia state employees
          </Link>
          .
        </P>

        <H3>CareFirst BlueCross BlueShield and Kaiser Permanente</H3>
        <P>
          Both serve Northern Virginia. CareFirst generally covers Wegovy on commercial plans with
          prior authorization at the BMI 30, or 27-with-comorbidity standard, typically on a higher
          cost-sharing tier. Kaiser Permanente of the Mid-Atlantic States requires prior
          authorization and has historically required enrollment in its own clinical pharmacy weight
          management program as a condition of coverage. Confirm current criteria directly with
          either plan.
        </P>

        <H3>The layer most people don&rsquo;t know about: your PBM</H3>
        <P>
          Your insurer decides your benefits, but a pharmacy benefit manager decides which specific
          drugs are on the covered list. Three PBMs &mdash; CVS Caremark, Express Scripts, and Optum
          Rx &mdash; handle most prescriptions in Virginia, and their decisions can override what
          you&rsquo;d expect from your insurer.
        </P>
        <P>
          <strong>The clearest example:</strong> CVS Caremark removed Zepbound from its standard
          commercial formulary in July 2025, pushing patients toward Wegovy. Many people had good
          results on Zepbound and were forced to switch; a class-action lawsuit followed.{" "}
          <strong>This is reversing.</strong> Eli Lilly announced on May 28, 2026 that Zepbound
          returns to CVS Caremark&rsquo;s formulary by <strong>October 1, 2026</strong>, with some
          commercially insured patients paying as little as $25. Express Scripts and Optum Rx
          already cover Zepbound. The caveat: being on a PBM formulary doesn&rsquo;t guarantee your
          plan covers it, because employers still make the final call.
        </P>

        <H2 id="marketplace">Individual and marketplace plans in Virginia</H2>
        <Callout>
          <strong>
            Virginia&rsquo;s Essential Health Benefits benchmark plan specifically excludes coverage
            for weight-loss drugs.
          </strong>{" "}
          A Joint Commission on Health Care report to the General Assembly put it directly:
          &ldquo;The current Virginia EHB benchmark plan specifically excludes coverage for weight
          loss drugs. Consequently, there are no individual or small group plans that cover weight
          loss drugs.&rdquo;
        </Callout>
        <P>
          This means Virginia individual and small-group plans are <strong>not required</strong> to
          cover Zepbound, Wegovy, or Saxenda for weight loss, and generally don&rsquo;t. Two things
          are worth noting. First, a plan may still cover semaglutide or tirzepatide for a{" "}
          <em>different</em> FDA-approved use &mdash; Wegovy for cardiovascular risk reduction,
          Wegovy for MASH liver disease, or Zepbound for moderate-to-severe obstructive sleep apnea.
          Second, Virginia&rsquo;s Bureau of Insurance filed a new benchmark application with CMS in
          May 2026 for plan year 2028, so this could change.
        </P>

        <H2 id="medications">Notes on each medication</H2>
        <H3>Zepbound (tirzepatide)</H3>
        <P>
          Approved for chronic weight management and, separately, for moderate-to-severe obstructive
          sleep apnea in adults with obesity. Adults 18 and older only. The sleep apnea indication is
          a meaningful alternative path with its own criteria &mdash; generally BMI of 30 or higher
          plus a documented AHI of 15 or more events per hour.
        </P>
        <P>
          <strong>Watch the device type.</strong> Zepbound comes as single-dose vials, the Pen, and
          the KwikPen, and plans do not treat them interchangeably. Sentara covers the Pens but
          excludes KwikPen and vial formulations; TRICARE&rsquo;s form specifies the Pen Injector;
          the Medicare Bridge covers the KwikPen. If your pharmacy claim rejects, the formulation may
          be the reason.
        </P>
        <H3>Wegovy (semaglutide)</H3>
        <P>
          The most broadly covered of the three across Virginia plans, and available as an injection,
          a tablet, and the higher-dose Wegovy HD. Wegovy carries{" "}
          <strong>three separate FDA-approved indications</strong>: reducing major cardiovascular
          events in adults with established cardiovascular disease and obesity or overweight;
          treating non-cirrhotic MASH with moderate-to-advanced fibrosis; and chronic weight
          management. The injection can be approved from age 12 in many plans; Wegovy HD and Wegovy
          tablets are adults only.
        </P>
        <H3>Saxenda (liraglutide)</H3>
        <UL>
          <li>
            <strong>A generic exists.</strong> Generic liraglutide launched in 2025 and is usually
            the covered option; brand Saxenda has had intermittent supply issues.
          </li>
          <li>
            <strong>Different renewal math.</strong> Most plans require 4% weight loss to continue
            rather than 5%.
          </li>
          <li>
            <strong>Shorter initial approvals.</strong> Cigna and UnitedHealthcare both approve
            Saxenda for 4 months initially.
          </li>
          <li>
            <strong>Daily, not weekly.</strong> Wegovy and Zepbound are weekly injections.
          </li>
          <li>
            <strong>Pediatric use.</strong> Approved from age 12 for patients weighing at least 60 kg
            (about 132 pounds).
          </li>
          <li>
            <strong>On TRICARE,</strong> Saxenda is a non-preferred product.
          </li>
        </UL>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="more">Other coverage guides</H2>
        <SpokeCards exclude="/medical-weight-loss/insurance/commercial-marketplace" />
      </PageSection>

      <InsuranceCTA />
    </>
  );
}
